/* ─── Embody ───
   Opens from whichever invitation was pressed (the product page's button, the launch popup's): a
   circular reveal expands from it and the water is already playing underneath. The panel is the
   card — opaque while it asks for a name, then the water flows inward from the button and the
   same card carries the pass.

   The download re-renders the card on a canvas at phone-portrait size, using the video's current
   frame as its background. That works because Shopify's CDN sends permissive CORS headers and the
   element carries crossorigin="anonymous", so the canvas is never tainted. */

(function () {
  var CREAM = '#FCF9F6';
  var INK = '#12050F';

  document.querySelectorAll('[id^="NvEmbody-"]').forEach(function (root) {
    var stage = root.querySelector('[data-nv-stage]');
    var video = root.querySelector('[data-nv-video]');
    var form = root.querySelector('[data-nv-form]');
    var details = root.querySelector('[data-nv-details]');
    var actions = root.querySelector('[data-nv-actions]');
    var card = root.querySelector('[data-nv-card]');
    var cardMedia = root.querySelector('[data-nv-card-media]');
    var canvas = root.querySelector('[data-nv-canvas]');
    var error = root.querySelector('[data-nv-error]');
    var title = root.querySelector('.nv-embody__title');
    if (!stage || !video || !form || !card || !canvas) return;

    var accent = root.dataset.accent || '#3FD8FF';
    var slate = root.dataset.slate || '';
    // The signup service (nutravey-mail): records the customer, keeps the card, sends the email.
    // Without it the form still posts to Shopify's own customer list, and nothing is emailed.
    var mailEndpoint = root.dataset.mail || '';
    var mailed = root.querySelector('[data-nv-mailed]');
    // Where the last drawing put the card on the canvas, for cropping it out.
    var frame = null;
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
    var pass = null;
    // Both windows show the same water as the stage: one in the submit button, one in the card.
    var windowVideo = root.querySelector('[data-nv-window]');
    var cardVideo = root.querySelector('[data-nv-card-video]');

    /* ─── Opening ───
       The overlay is clipped to the button's exact rectangle and corner, then opened to the full
       screen; a ring rides the same geometry so the edge stays lit as it travels. Nothing fades:
       the water is uncovered by the shape the visitor pressed. */
    /* Every measurement is taken against the overlay's own box rather than window.innerWidth
       and innerHeight: on phones the browser chrome comes and goes and those two lag behind
       it, and the clip, the ring and the pinned water all drifted off the button by that gap. */
    var frameOf = function () {
      var r = root.getBoundingClientRect();
      return r.width && r.height ? r : { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight, right: window.innerWidth, bottom: window.innerHeight };
    };

    /* ─── The shape's edges ───
       Four insets and a corner radius, kept on the overlay as custom properties; the clip and the
       ring are both drawn from them (see the stylesheet), so the lit edge sits on the shape's edge
       in every frame. A small tween here sets them frame by frame. They were CSS transitions on
       registered properties, and phone Safari moved the ring along those but never redrew the
       clip: closing left the screen full of water while an empty outline slid down onto the
       button. Set from the script, the two cannot come apart. */
    var EDGE_MS = 820;
    var OPEN_EDGES = [0, 0, 0, 0, 0];
    var edges = null;
    var edgeFrame = null;
    var setEdges = function (next) {
      edges = next;
      root.style.setProperty('--ct', next[0] + 'px');
      root.style.setProperty('--cr', next[1] + 'px');
      root.style.setProperty('--cb', next[2] + 'px');
      root.style.setProperty('--cl', next[3] + 'px');
      root.style.setProperty('--crad', next[4] + 'px');
    };
    // cubic-bezier(0.65, 0, 0.35, 1), the curve the rest of the overlay moves on.
    var ease = (function (x1, y1, x2, y2) {
      var at = function (t, a, b) {
        return (1 - 3 * b + 3 * a) * t * t * t + (3 * b - 6 * a) * t * t + 3 * a * t;
      };
      return function (x) {
        if (x <= 0) return 0;
        if (x >= 1) return 1;
        var lo = 0, hi = 1, t = x;
        for (var i = 0; i < 20; i++) {
          if (at(t, x1, x2) < x) lo = t; else hi = t;
          t = (lo + hi) / 2;
        }
        return at(t, y1, y2);
      };
    })(0.65, 0, 0.35, 1);
    var stopEdges = function () {
      if (edgeFrame) cancelAnimationFrame(edgeFrame);
      edgeFrame = null;
    };
    /* Takes the edges to `to` over EDGE_MS, then calls done. A new move drops the one in flight,
       from wherever it has got to, and that one's done is never called. */
    var moveEdges = function (to, done) {
      stopEdges();
      var from = edges || to;
      if (reduced.matches) {
        setEdges(to);
        done();
        return;
      }
      var start = null;
      var step = function (now) {
        if (start === null) start = now;
        var p = ease((now - start) / EDGE_MS);
        setEdges(from.map(function (v, i) { return v + (to[i] - v) * p; }));
        if (p < 1) {
          edgeFrame = requestAnimationFrame(step);
          return;
        }
        edgeFrame = null;
        done();
      };
      edgeFrame = requestAnimationFrame(step);
    };

    /* A button's rectangle and corner, as the overlay's edges. */
    var shapeOf = function (button) {
      var box = button.getBoundingClientRect();
      var vr = frameOf();
      // Kept on screen: if the button has scrolled away, the shape lands at the nearest edge
      // instead of flying off it or collapsing to nothing.
      var h = Math.min(box.height, vr.height), w = Math.min(box.width, vr.width);
      var top = Math.max(vr.top, Math.min(box.top, vr.bottom - h));
      var left = Math.max(vr.left, Math.min(box.left, vr.right - w));
      // The radius the button actually shows, not its declared 999px: animating from 999px kept
      // the shape an oval until the last frames, then the corners snapped square.
      var rad = Math.min(parseFloat(getComputedStyle(button).borderRadius) || 0, h / 2, w / 2);
      return [top - vr.top, vr.right - (left + w), vr.bottom - (top + h), left - vr.left, rad];
    };

    var origin = null;
    var closeTimer = null;
    /* The launch popup follows the overlay through these: it gives the keyboard back to its own
       button when the card closes into it, and stays away for anyone who has made a card. */
    var announce = function (state) {
      document.dispatchEvent(new CustomEvent('nv:embody', {
        detail: { state: state, origin: origin, filled: root.classList.contains('is-filled') },
      }));
    };
    var lockY = null;
    // Phones ignore overflow:hidden, so the page behind is pinned in place instead and put back
    // exactly where it was, without the smooth-scroll the theme sets on html.
    var lockPage = function () {
      if (lockY !== null) return;
      lockY = window.scrollY;
      var b = document.body.style;
      b.position = 'fixed'; b.top = -lockY + 'px'; b.left = '0'; b.right = '0'; b.width = '100%';
    };
    var unlockPage = function () {
      if (lockY === null) return;
      var b = document.body.style;
      b.position = ''; b.top = ''; b.left = ''; b.right = ''; b.width = '';
      var html = document.documentElement, prev = html.style.scrollBehavior;
      html.style.scrollBehavior = 'auto';
      window.scrollTo(0, lockY);
      html.style.scrollBehavior = prev;
      lockY = null;
    };
    var previews = [];

    /* Phones play the portrait clip, so the still they see before it moves must be the portrait
       one too; a landscape still switching to portrait water read as a glitch at the start. */
    var portrait = window.matchMedia('(max-aspect-ratio: 1/1)');
    var facePoster = function (v) {
      if (!v) return;
      var p = portrait.matches ? v.dataset.posterPortrait : v.dataset.posterLandscape;
      if (p) v.poster = p;
    };
    facePoster(video);

    var gate = window.nvVideo;

    var openFrom = function (button) {
      // 7% of a laptop's width is right; on a phone that is under 30px, so narrow screens use
      // a steeper scale.
      var w = window.innerWidth;
      var size = w < 600 ? Math.max(44, Math.min(64, w * 0.135)) : Math.max(30, Math.min(80, w * 0.07));
      root.style.setProperty('--poster-size', size.toFixed(1) + 'px');
      root.style.setProperty('--accent', accent);
      origin = button;
      clearTimeout(closeTimer);
      stopEdges();
      root.classList.remove('is-closing', 'is-landing');
      // Shown first so the overlay has a box to measure against; nothing paints before the clip
      // is set, because all of this runs before the browser gets a frame. Placing also holds the
      // contents' own transitions and the ring still until the button's shape is in place.
      root.classList.add('is-placing');
      root.hidden = false;
      // The launch popup is an invitation to sign up, so it opens on the card itself, past the
      // statement; every button does once the card is made, since the card is what they come
      // back for. Set before the reveal, so the shape uncovers the card directly.
      if (scroller && (button.dataset.nvEmbodyOpen === 'card' || root.classList.contains('is-filled'))) {
        var cardAct = root.querySelector('.nv-embody__act--card');
        if (cardAct) scroller.scrollTop = cardAct.offsetTop;
      }
      var vr = frameOf();
      setEdges(shapeOf(button));
      void root.offsetWidth;
      root.classList.remove('is-placing');
      // The stage's resting circle is given in pixels from the start — a full diagonal, which
      // covers the screen from any centre — so the fill later blends pixels with pixels. Blending
      // from the stylesheet's percentage fallback produced a calc() mid-flight, which is exactly
      // the kind of value phone Safari has been dropping.
      root.style.setProperty('--kx', vr.width / 2 + 'px');
      root.style.setProperty('--ky', vr.height / 2 + 'px');
      root.style.setProperty('--kr0', Math.ceil(Math.hypot(vr.width, vr.height)) + 'px');
      root.classList.add('is-opening');
      // The page's scrollbar is taken away only where there is one to take: on phones the lock
      // does nothing useful and toggling it can throw the page's scroll position around.
      if (finePointer.matches) {
        var bar = window.innerWidth - document.documentElement.clientWidth;
        document.documentElement.style.overflow = 'hidden';
        if (bar > 0) document.documentElement.style.paddingRight = bar + 'px';
      } else lockPage();
      // The button's own water is under the overlay now; a phone has only so many decoders.
      // Before it stops, the overlay's water is brought level with it, so the expansion carries
      // on from the frame the button was showing rather than starting the clip again.
      var preview = button.querySelector('video');
      if (preview && preview.currentTime > 0.2 && video.readyState >= 1) {
        try {
          video.currentTime = preview.currentTime;
        } catch (e) {}
      }
      previews.forEach(function (each) {
        each.pause();
      });
      // One clip during the reveal: the stage. The windows in the card start later, when the
      // card is on screen (below), by which time the file is in the cache. Three fetches and
      // three decoders starting on the tap was what left the water frozen on phones.
      if (!root.classList.contains('is-filled')) play(video);
      // A card kept from an earlier page: nothing has started its water on this one.
      else play(cardVideo);
      startHold();

      // The label stays where it is and softens out as the shape grows over it.
      button.classList.add('is-opening');
      announce('open');

      // Two frames: the button's shape has to be painted before the shape can grow from it.
      void root.offsetWidth;
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          // Closed, or opened from elsewhere, before it got going.
          if (origin !== button || !root.classList.contains('is-opening')) return;
          root.classList.add('is-open');
          pin();
          moveEdges(OPEN_EDGES, function () {
            root.classList.remove('is-opening');
            button.classList.remove('is-opening');
          });
        });
      });
    };

    /* Closing runs the opening backwards: the lit ring reappears and the whole screen draws back
       into the button it came from, measured again in case the viewport has changed. */
    var close = function () {
      if (!root.classList.contains('is-open')) return;
      // Closed, the link's #embody is dropped so a reload doesn't reopen it and the same link
      // followed again still fires.
      if (location.hash.toLowerCase() === '#embody') {
        history.replaceState(null, '', location.pathname + location.search);
      }
      // The page is given back its scroll now, while the overlay still covers all of it: the
      // reflow that comes with it (scrollbar returning, the phone's pinned body released) used
      // to happen after the shape had landed on the button, in full view, as a flicker of the
      // text behind.
      document.documentElement.style.overflow = '';
      document.documentElement.style.paddingRight = '';
      unlockPage();
      root.classList.add('is-closing');
      root.classList.remove('is-open', 'is-opening');
      origin.classList.remove('is-opening');
      // The button's own water is back under the shape before it lands, so there is no cut.
      // Only buttons that are showing: the launch popup's stays still while it is not up.
      previews.forEach(function (each) {
        if (each.getClientRects().length) play(each);
      });
      moveEdges(shapeOf(origin), function () {
        // Landed on the button: the overlay dissolves into it rather than cutting out.
        root.classList.add('is-landing');
        clearTimeout(closeTimer);
        closeTimer = setTimeout(function () {
          root.classList.remove('is-closing', 'is-landing');
          if (root.classList.contains('is-open')) return;
          root.hidden = true;
          // Reopening should start at the statement again — unless the card is made, in which
          // case it is the card they come back to.
          if (scroller && !root.classList.contains('is-filled')) scroller.scrollTop = 0;
          announce('closed');
        }, 180);
      });
    };

    document.querySelectorAll('[data-nv-embody-open]').forEach(function (button) {
      var preview = button.querySelector('video');
      if (preview) {
        previews.push(preview);
        // The still under it is the button until the clip is actually moving.
        preview.addEventListener('playing', function () { preview.classList.add('is-playing'); });
        // Starts as the button nears the screen; a still is what it shows until then.
        if ('IntersectionObserver' in window) {
          var near = new IntersectionObserver(function (entries) {
            if (!entries.some(function (e) { return e.isIntersecting; })) return;
            near.disconnect();
            play(preview);
          }, { rootMargin: '200px 0px' });
          near.observe(preview);
        } else play(preview);
      }
      button.addEventListener('click', function () {
        openFrom(button);
      });
    });

    var scrollDown = root.querySelector('[data-nv-scroll-down]');
    if (scrollDown) {
      scrollDown.addEventListener('click', function () {
        var target = root.querySelector('.nv-embody__act--card');
        if (target) target.scrollIntoView({ behavior: reduced.matches ? 'auto' : 'smooth' });
      });
    }

    var closeButton = root.querySelector('[data-nv-close]');
    if (closeButton) closeButton.addEventListener('click', close);
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && root.classList.contains('is-open')) close();
    });

    /* Autoplay is refused in iOS Low Power Mode and under reduced-motion; both fall back to the
       poster, which is a frame of this same clip. */
    function play(element) {
      if (!element || reduced.matches || element.dataset.nvGaveUp) return;
      // On a slow connection the water is never fetched; the poster is a frame of it.
      if (gate && gate.slow) return;
      // A little under real time: water at full speed reads as restless.
      element.playbackRate = 0.7;
      if (gate) gate.watch(element);
      var attempt = element.play();
      if (attempt && attempt.catch) attempt.catch(function () {});
    }

    /* ─── Pinning the windows to the viewport ───
       Each window video is sized to the viewport and shifted by its host's own position, so it
       shows the same patch of water as the stage behind it. Recomputed whenever the host can have
       moved: the overlay scrolling, the window resizing, the card tilting. */
    var scroller = root.querySelector('[data-nv-scroll]');
    var pinFrame = null;
    var pin = function () {
      pinFrame = null;
      var vr = frameOf();
      [windowVideo, cardVideo].forEach(function (v) {
        if (!v || !v.parentElement) return;
        var r = v.parentElement.getBoundingClientRect();
        v.style.width = vr.width + 'px';
        v.style.height = vr.height + 'px';
        v.style.left = vr.left - r.left + 'px';
        v.style.top = vr.top - r.top + 'px';
      });
    };
    var requestPin = function () {
      if (!pinFrame) pinFrame = requestAnimationFrame(pin);
    };
    if (scroller) scroller.addEventListener('scroll', requestPin, { passive: true });
    /* The card's two windows start only once the card is on screen inside the overlay, one
       after the other: the button's window first, the card's own (under its cover) after it. */
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        if (!entries.some(function (e) { return e.isIntersecting; })) return;
        if (root.classList.contains('is-filled')) return;
        facePoster(windowVideo);
        play(windowVideo);
        setTimeout(function () { if (!root.hidden) play(cardVideo); }, 600);
      }, { root: scroller, threshold: 0.2 }).observe(card);
    }
    window.addEventListener('resize', requestPin);
    window.addEventListener('orientationchange', requestPin);
    card.addEventListener('pointermove', requestPin);

    /* The button's window is meant to be the water behind the card showing through, so while
       the overlay is open it is kept to the frame: re-pinned every frame (the card's tilt moves
       it), and pulled back onto the stage's clock whenever it drifts. */
    var holdFrame = null;
    var hold = function () {
      if (root.hidden || root.classList.contains('is-filled')) { holdFrame = null; return; }
      pin();
      if (windowVideo && !windowVideo.paused && video.readyState >= 2) {
        if (windowVideo.playbackRate !== video.playbackRate) windowVideo.playbackRate = video.playbackRate;
        if (Math.abs(windowVideo.currentTime - video.currentTime) > 0.08) {
          try { windowVideo.currentTime = video.currentTime; } catch (e) {}
        }
      }
      holdFrame = requestAnimationFrame(hold);
    };
    var startHold = function () { if (!holdFrame) holdFrame = requestAnimationFrame(hold); };
    card.addEventListener('pointerleave', function () {
      setTimeout(requestPin, 220);
    });

    /* ─── The statement's sheen, at the pointer ─── */
    if (title && finePointer.matches) {
      // Its own name: `frame` is the card's place on the canvas (above), and sharing it froze the
      // sheen once the card was drawn and could leave the emailed card unsent.
      var sheenFrame = null;
      var point = null;
      var apply = function () {
        sheenFrame = null;
        var box = title.getBoundingClientRect();
        root.style.setProperty('--hx', (((point.x - box.left) / box.width) * 100).toFixed(2) + '%');
        root.style.setProperty('--hy', (((point.y - box.top) / box.height) * 100).toFixed(2) + '%');
        var art = root.querySelector('.nv-embody__script.is-art');
        if (art) {
          var ab = art.getBoundingClientRect();
          root.style.setProperty('--sx', (((point.x - ab.left) / ab.width) * 100).toFixed(2) + '%');
          root.style.setProperty('--sy', (((point.y - ab.top) / ab.height) * 100).toFixed(2) + '%');
        }
        root.style.setProperty('--mx', point.x.toFixed(0) + 'px');
        root.style.setProperty('--my', point.y.toFixed(0) + 'px');
        root.style.setProperty('--nv-holo', point.holo.toFixed(3));
      };
      var request = function (next) {
        point = next;
        if (!sheenFrame) sheenFrame = requestAnimationFrame(apply);
      };
      root.addEventListener('pointermove', function (event) {
        if (event.pointerType !== 'mouse') return;
        root.classList.add('is-holo-live');
        request({ x: event.clientX, y: event.clientY, holo: 1 });
      });
      root.addEventListener('pointerleave', function (event) {
        root.classList.remove('is-holo-live');
        request({ x: event.clientX, y: event.clientY, holo: 0 });
      });
    }

    /* ─── The card's tilt, as on the Next.js pass ───
       A mouse tilts it under the pointer. On phones the gyroscope does — iOS only grants that
       from inside a tap, so the ask rides on the submit — and a finger dragged across the card
       tilts it too, for anyone who declines. */
    var tilt = function (tx, ty) {
      if (card.classList.contains('is-turning')) return;
      card.style.setProperty('--tx', tx.toFixed(2) + 'deg');
      card.style.setProperty('--ty', ty.toFixed(2) + 'deg');
      requestPin();
    };
    var gyro = false;
    var onOrientation = function (event) {
      if (event.beta === null || event.gamma === null) return;
      // beta: front/back (−180..180), gamma: left/right (−90..90). Centred on a hand-held rest.
      var tx = Math.max(-12, Math.min(12, (event.beta - 40) * -0.4));
      var ty = Math.max(-12, Math.min(12, event.gamma * 0.5));
      tilt(tx, ty);
    };
    var enableGyro = function () {
      if (gyro || reduced.matches || finePointer.matches || !window.DeviceOrientationEvent) return;
      var start = function () {
        gyro = true;
        window.addEventListener('deviceorientation', onOrientation);
      };
      if (typeof DeviceOrientationEvent.requestPermission === 'function') {
        DeviceOrientationEvent.requestPermission().then(function (state) {
          if (state === 'granted') start();
        }).catch(function () {});
      } else {
        start();
      }
    };

    card.addEventListener('pointermove', function (event) {
      if (reduced.matches || card.classList.contains('is-turning')) return;
      if (event.pointerType !== 'mouse' && event.pointerType !== 'touch') return;
      var box = card.getBoundingClientRect();
      var x = event.clientX - box.left - box.width / 2;
      var y = event.clientY - box.top - box.height / 2;
      card.style.setProperty('--tx', ((y / (box.height / 2)) * -10).toFixed(2) + 'deg');
      card.style.setProperty('--ty', ((x / (box.width / 2)) * 10).toFixed(2) + 'deg');
    });
    card.addEventListener('pointerleave', function () {
      card.style.setProperty('--tx', '0deg');
      card.style.setProperty('--ty', '0deg');
    });

    var makeId = function () {
      var chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
      var out = '';
      for (var i = 0; i < 6; i++) out += chars[Math.floor(Math.random() * chars.length)];
      return out.slice(0, 3) + '-' + out.slice(3);
    };

    /* Keeps the first character and the domain: the owner recognises their own card, a stranger
       reading a screenshot has nothing to write to. */
    var maskEmail = function (email) {
      var at = email.lastIndexOf('@');
      if (at < 1) return '***';
      var local = email.slice(0, at);
      var stars = new Array(Math.max(3, Math.min(local.length - 1, 8)) + 1).join('*');
      return local.charAt(0) + stars + email.slice(at);
    };

    var setText = function (selector, value) {
      var el = root.querySelector(selector);
      if (el) el.textContent = value;
    };

    /* ─── The card, kept ───
       Once the list has the signup, the card is kept in this browser, so the overlay opens on it
       from any button on any later page instead of asking for a name again. Storage can be
       missing or refuse writes (private windows, blocked site data); the prompt then comes back,
       and the claim answers with the id already on record for that email. */
    var KEPT = 'nv-embody-pass';
    var keep = function (entry) {
      try {
        localStorage.setItem(KEPT, JSON.stringify({ id: entry.id, name: entry.name, email: entry.email, customer: entry.customer || '' }));
      } catch (e) {}
    };
    var kept = function () {
      try {
        var entry = JSON.parse(localStorage.getItem(KEPT) || 'null');
        return entry && entry.id && entry.name && entry.email ? entry : null;
      } catch (e) {
        return null;
      }
    };

    // The prompt gives way to the details: the same card, now the pass.
    var present = function (entry) {
      setText('[data-nv-card-name]', entry.name);
      setText('[data-nv-card-email]', entry.email);
      setText('[data-nv-card-id]', entry.id);
      form.hidden = true;
      if (details) details.hidden = false;
      if (actions) actions.hidden = false;
      // The gyroscope ask is a system dialog on iPhones; raised on the submit it would sit on top
      // of the water coming in. It waits for the first touch on the finished card instead.
      card.addEventListener('click', function (event) {
        if (event.target.closest('button')) return;
        enableGyro();
      });
    };

    /* ─── The water enters the card ───
       The overlay's video is moved into the card and revealed with a circle grown from the button
       that was just pressed, so the water arrives from where the press happened. */
    /* ─── The water moves into the card ───
       The page's water closes down onto the card's rectangle, uncovering the grid behind it, while
       the card's own window opens from the button that was just pressed. Both show the same
       viewport-pinned picture, so it reads as one body of water finding its way in. */
    var fill = function () {
      var box = card.getBoundingClientRect();

      // The page's water contracts as a circle centred on the card, down to the circle that just
      // covers it; at that moment the card's own water takes over and closes from its edges to
      // its centre, so the movement never reverses direction.
      var ccx = box.left + box.width / 2;
      var ccy = box.top + box.height / 2;
      var enclosing = Math.hypot(box.width, box.height) / 2;
      // Only the centre moves here; the resting radius was set at opening and still covers the
      // screen from the card's centre, so this change is invisible until the class lands.
      root.style.setProperty('--kx', ccx + 'px');
      root.style.setProperty('--ky', ccy + 'px');
      root.style.setProperty('--kr', enclosing.toFixed(0) + 'px');

      pin();
      // The button's window is gone with the form; the card's water has been playing under its
      // cover since the overlay opened and only needs bringing level with the page's.
      if (windowVideo) windowVideo.pause();
      if (cardVideo) {
        play(cardVideo);
        try {
          cardVideo.currentTime = video.currentTime;
        } catch (e) {}
      }

      // The resting state must be painted before it can be animated away from.
      void cardMedia.offsetWidth;
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          root.classList.add('is-filled');
        });
      });
      setTimeout(function () {
        cardMedia.classList.add('is-open');
      }, 1000);
      // Faded out by then; the card's water is the only one left running.
      setTimeout(function () {
        if (root.classList.contains('is-filled')) video.pause();
      }, 1400);
    };

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      var name = form.querySelector('[data-nv-name]').value.trim();
      var email = form.querySelector('[data-nv-email]').value.trim();
      if (!name || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        if (error) error.textContent = 'A name, and an email we can reach you on.';
        return;
      }

      pass = { id: makeId(), name: name, email: email };
      // The claim answers with the id on record — the first card this address ever took — and
      // the customer's number, so the card that follows is filed against the same record.
      var claimed = claim(pass).then(function (json) {
        // Nothing back means the list was not reached: no card is kept, so they can try again.
        if (!json) return;
        if (json.customer) pass.customer = json.customer;
        if (json.id && json.id !== pass.id) {
          pass.id = json.id;
          setText('[data-nv-card-id]', pass.id);
        }
        keep(pass);
      });
      // The card is drawn and sent once the water has settled into it and the claim has landed.
      var settled = new Promise(function (resolve) {
        setTimeout(resolve, 2000);
      });
      Promise.all([claimed, settled]).then(sendCard);

      // The keyboard goes first and the card takes its final shape before anything is measured:
      // filling from the prompter's geometry aimed the water at where the card used to be.
      if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
      present(pass);
      requestAnimationFrame(fill);
      announce('filled');
    });

    /* The signup itself. Through the signup service when one is configured — it writes the
       customer through the Admin API, so the record is certain and carries the card id. Otherwise
       Shopify's own customer form endpoint, the one the footer posts to. Either way everyone
       lands in one list and can be emailed on launch day. */
    var listFailed = function () {
      if (error) {
        error.hidden = false;
        error.textContent = 'Your card is ready, but we could not reach the list — email us and we will add you.';
      }
    };
    var post = function (payload) {
      return fetch(mailEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }).then(function (response) {
        return response.json().then(function (json) {
          if (!response.ok) throw new Error((json && json.error) || String(response.status));
          return json;
        });
      });
    };
    var claim = function (entry) {
      if (mailEndpoint) {
        return post({ action: 'claim', name: entry.name, email: entry.email, id: entry.id }).catch(listFailed);
      }
      var body = new FormData();
      body.append('form_type', 'customer');
      body.append('utf8', '✓');
      body.append('contact[tags]', 'newsletter,embody,first-batch');
      body.append('contact[email]', entry.email);
      body.append('contact[first_name]', entry.name);
      return fetch((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/', {
        method: 'POST',
        body: body,
      }).catch(listFailed);
    };

    /* ─── The email's hero ───
       Logo, the script, RESERVED FOR the name and the line under it, on the same frame of water
       as the card, rendered here where the brand's faces are loaded. Mail clients get it as a
       picture: it looks the same in every one of them, and it survives Gmail's dark mode, which
       inverts type but leaves pictures alone. */
    var scriptArt = null;
    var loadScriptArt = function () {
      if (scriptArt) return scriptArt;
      scriptArt = fetch(root.dataset.script || '', { mode: 'cors' })
        .then(function (response) {
          return response.ok ? response.text() : Promise.reject(new Error('script'));
        })
        .then(function (svg) {
          return new Promise(function (resolve, reject) {
            var img = new Image();
            img.onload = function () {
              resolve(img);
            };
            img.onerror = reject;
            img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg.replace(/fill="#000"/g, 'fill="#FCF9F6"'));
          });
        })
        .catch(function () {
          return null;
        });
      return scriptArt;
    };

    /* The water the hero is drawn on: the centre of the backdrop that runs behind the whole
       email, cut from the same still at twice the size, so the hero and the backdrop meet
       without a seam on wide screens. Lives in the store's Files. */
    var HERO_BASE = 'https://cdn.shopify.com/s/files/1/0697/7522/8995/files/nutravey-embody-hero-base.jpg';
    var heroBase = null;
    var loadHeroBase = function () {
      if (heroBase) return heroBase;
      heroBase = new Promise(function (resolve) {
        var img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = function () {
          resolve(img);
        };
        img.onerror = function () {
          resolve(null);
        };
        img.src = HERO_BASE;
      });
      return heroBase;
    };

    var fontsReady = function () {
      if (!document.fonts || !document.fonts.load) return Promise.resolve();
      var wanted = Promise.all([document.fonts.load('700 48px LEMONMILK'), document.fonts.load('400 26px "JetBrains Mono"')]);
      return Promise.race([wanted, new Promise(function (r) { setTimeout(r, 1500); })]).catch(function () {});
    };

    var drawHero = function (entry, base) {
      var W = 1200;
      var H = 820;
      var c = document.createElement('canvas');
      c.width = W;
      c.height = H;
      var g = c.getContext('2d');
      g.fillStyle = '#07111C';
      g.fillRect(0, 0, W, H);
      if (base) {
        // The backdrop's own centre, veil included: nothing else is drawn under the type.
        g.drawImage(base, 0, 0, W, H);
      } else {
        // Without it, the water the card has, veiled here to the same depth.
        var source = cardVideo && cardVideo.videoWidth ? cardVideo : video.videoWidth ? video : null;
        if (source) {
          var s = Math.max(W / source.videoWidth, H / source.videoHeight);
          var dw = source.videoWidth * s;
          var dh = source.videoHeight * s;
          g.drawImage(source, (W - dw) / 2, (H - dh) / 2, dw, dh);
        }
        var scrim = g.createLinearGradient(0, 0, 0, H);
        scrim.addColorStop(0, 'rgba(7,17,28,0.34)');
        scrim.addColorStop(1, 'rgba(7,17,28,0.6)');
        g.fillStyle = scrim;
        g.fillRect(0, 0, W, H);
      }

      g.textAlign = 'center';
      g.textBaseline = 'alphabetic';
      // Sized for the phone, where this picture shows at a third of its pixels.
      var line = 'RESERVED FOR ' + entry.name.toUpperCase();
      var size = 60;
      g.letterSpacing = '2px';
      do {
        size -= 2;
        g.font = '700 ' + size + 'px LEMONMILK, Helvetica, Arial, sans-serif';
      } while (g.measureText(line).width > W - 120 && size > 28);
      g.fillStyle = '#FCF9F6';
      g.fillText(line, W / 2, 482);
      g.font = '400 33px "JetBrains Mono", Menlo, monospace';
      g.letterSpacing = '0.5px';
      g.fillStyle = 'rgba(252,249,246,0.84)';
      g.fillText('Your place in the first batch is kept.', W / 2, 566);
      g.fillText('Your card is below.', W / 2, 616);
      g.letterSpacing = '0px';

      var logo = root.querySelector('.nv-embody__face--front svg');
      var jobs = [
        loadScriptArt().then(function (img) {
          if (img) g.drawImage(img, (W - 660) / 2, 220, 660, 169);
        }),
      ];
      if (logo) jobs.push(drawLogo(g, logo, (W - 290) / 2, 80, 290, 53));
      return Promise.all(jobs).then(function () {
        return c.toDataURL('image/jpeg', 0.86);
      });
    };

    /* The card, as drawn for the download but cut out on its own with nothing behind it, goes to
       the service as a transparent PNG together with the hero; it keeps both and Shopify sends
       the welcome email around them. Transparent, so in the email the glow falls onto the grid
       and the grid runs on under it. Drawn at mail size (twice the 340px it shows at) and
       without grain, which is what keeps a PNG small enough to send from a phone. */
    var sendCard = function () {
      if (!mailEndpoint || !pass) return;
      var entry = pass;
      Promise.all([draw({ grain: false }), fontsReady()])
        .then(function () {
          if (!frame) return;
          // The margin has to hold the whole glow: a canvas shadow of 90 reaches about 130px
          // before it is gone, and a narrower margin cut it off in a straight line.
          var margin = 160;
          var scale = 800 / (frame.cw + margin * 2);
          var crop = document.createElement('canvas');
          crop.width = 800;
          crop.height = Math.round((frame.ch + margin * 2) * scale);
          var g = crop.getContext('2d');
          g.scale(scale, scale);
          g.save();
          g.shadowColor = accent;
          // Shadow blur is measured in device pixels, untouched by the scale.
          g.shadowBlur = 90 * scale;
          g.fillStyle = INK;
          roundRect(g, margin, margin, frame.cw, frame.ch, frame.radius);
          g.fill();
          g.restore();
          g.save();
          roundRect(g, margin, margin, frame.cw, frame.ch, frame.radius);
          g.clip();
          g.drawImage(canvas, frame.cx, frame.cy, frame.cw, frame.ch, margin, margin, frame.cw, frame.ch);
          g.restore();
          var card = crop.toDataURL('image/png');
          return loadHeroBase().then(function (base) {
            return drawHero(entry, base);
          }).then(function (hero) {
            return post({
              action: 'card',
              email: entry.email,
              id: entry.id,
              name: entry.name,
              customer: entry.customer || '',
              card: card,
              hero: hero,
            });
          });
        })
        .then(function (json) {
          if (json && json.mailed && mailed) {
            mailed.textContent = 'A copy is on its way to ' + entry.email + '.';
            mailed.hidden = false;
          }
        })
        .catch(function () {});
    };

    /* The faces swap when the turn actually crosses 90°, read off the card's transform each
       frame, so the swap lands mid-turn whatever the easing does. */
    var turning = null;
    var turnFrame = null;
    var watchTurn = function () {
      var m = new DOMMatrix(getComputedStyle(card).transform);
      var angle = Math.abs((Math.atan2(m.m13, m.m11) * 180) / Math.PI);
      card.classList.toggle('is-showing-back', angle > 90);
      turnFrame = requestAnimationFrame(watchTurn);
    };
    root.querySelectorAll('[data-nv-flip]').forEach(function (button) {
      button.addEventListener('click', function () {
        card.style.setProperty('--tx', '0deg');
        card.style.setProperty('--ty', '0deg');
        card.classList.add('is-turning');
        card.classList.toggle('is-flipped');
        cancelAnimationFrame(turnFrame);
        turnFrame = requestAnimationFrame(watchTurn);
        clearTimeout(turning);
        turning = setTimeout(function () {
          cancelAnimationFrame(turnFrame);
          card.classList.remove('is-turning');
          card.classList.toggle('is-showing-back', card.classList.contains('is-flipped'));
        }, 950);
      });
    });

    /* ─── The download ───
       Rather than a second hand-built layout that drifts from the first, this measures the card
       on screen and redraws it at scale: every string is placed where its element sits, in the
       font, size, letter-spacing and colour the browser computed for it. So the file matches what
       the visitor is looking at, including the frame of water behind it. */
    var roundRect = function (ctx, x, y, w, h, r) {
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.arcTo(x + w, y, x + w, y + h, r);
      ctx.arcTo(x + w, y + h, x, y + h, r);
      ctx.arcTo(x, y + h, x, y, r);
      ctx.arcTo(x, y, x + w, y, r);
      ctx.closePath();
    };

    // The card's own SVG wordmark, rasterised so the download carries the real mark.
    var drawLogo = function (ctx, svg, x, y, w, h) {
      return new Promise(function (resolve) {
        var clone = svg.cloneNode(true);
        clone.setAttribute('width', Math.round(w));
        clone.setAttribute('height', Math.round(h));
        clone.setAttribute('fill', '#FCF9F6');
        var markup = new XMLSerializer().serializeToString(clone);
        var img = new Image();
        img.onload = function () {
          ctx.drawImage(img, x, y, w, h);
          resolve();
        };
        img.onerror = function () {
          resolve();
        };
        img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(markup);
      });
    };

    // A 160px tile of monochrome noise, built once.
    var grainTile = null;
    var grainPattern = function (ctx) {
      if (!grainTile) {
        grainTile = document.createElement('canvas');
        grainTile.width = grainTile.height = 160;
        var g = grainTile.getContext('2d');
        var img = g.createImageData(160, 160);
        for (var i = 0; i < img.data.length; i += 4) {
          var v = 96 + Math.floor(Math.random() * 64);
          img.data[i] = img.data[i + 1] = img.data[i + 2] = v;
          img.data[i + 3] = 140;
        }
        g.putImageData(img, 0, 0);
      }
      return ctx.createPattern(grainTile, 'repeat');
    };

    var draw = function (opts) {
      opts = opts || {};
      var ctx = canvas.getContext('2d');
      var W = canvas.width;
      var H = canvas.height;
      var face = root.querySelector('.nv-embody__face--front');
      var box = card.getBoundingClientRect();

      // The card keeps its on-screen proportions, centred in a phone-portrait frame.
      var cw = Math.round(W * 0.86);
      var k = cw / box.width;
      var ch = Math.round(box.height * k);
      var cx = Math.round((W - cw) / 2);
      var cy = Math.round((H - ch) / 2);
      var radius = parseFloat(getComputedStyle(face).borderRadius) * k;
      frame = { cx: cx, cy: cy, cw: cw, ch: ch, radius: radius };

      ctx.clearRect(0, 0, W, H);
      ctx.fillStyle = INK;
      ctx.fillRect(0, 0, W, H);

      ctx.save();
      ctx.shadowColor = accent;
      ctx.shadowBlur = 90;
      ctx.fillStyle = INK;
      roundRect(ctx, cx, cy, cw, ch, radius);
      ctx.fill();
      ctx.restore();

      ctx.save();
      roundRect(ctx, cx, cy, cw, ch, radius);
      ctx.clip();

      var source = cardVideo && cardVideo.videoWidth ? cardVideo : (video.videoWidth ? video : null);
      if (source) {
        var scale = Math.max(cw / source.videoWidth, ch / source.videoHeight);
        var dw = source.videoWidth * scale;
        var dh = source.videoHeight * scale;
        // The grade the card carries on screen, so the file is not the brighter version of it.
        ctx.filter = 'brightness(1.14) saturate(1.06) contrast(1.02)';
        ctx.drawImage(source, cx + (cw - dw) / 2, cy + (ch - dh) / 2, dw, dh);
        ctx.filter = 'none';
      }

      var scrim = ctx.createLinearGradient(0, cy, 0, cy + ch);
      scrim.addColorStop(0, 'rgba(18,5,15,0.18)');
      scrim.addColorStop(0.4, 'rgba(18,5,15,0.42)');
      scrim.addColorStop(0.62, 'rgba(18,5,15,0.78)');
      scrim.addColorStop(1, 'rgba(18,5,15,0.92)');
      ctx.fillStyle = scrim;
      ctx.fillRect(cx, cy, cw, ch);

      var sheen = ctx.createLinearGradient(cx, cy + ch, cx + cw, cy);
      sheen.addColorStop(0.3, 'rgba(94,240,255,0)');
      sheen.addColorStop(0.45, 'rgba(94,240,255,0.12)');
      sheen.addColorStop(0.55, 'rgba(255,94,247,0.14)');
      sheen.addColorStop(0.7, 'rgba(160,255,140,0)');
      ctx.fillStyle = sheen;
      ctx.fillRect(cx, cy, cw, ch);

      // Grain, as on the card: a tiled noise pattern in overlay. Left off for the emailed copy,
      // which travels as a PNG — noise is what makes a PNG heavy, and at mail size it is unseen.
      if (opts.grain !== false) {
        ctx.globalCompositeOperation = 'overlay';
        ctx.globalAlpha = 0.55;
        ctx.fillStyle = grainPattern(ctx);
        ctx.fillRect(cx, cy, cw, ch);
        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = 'source-over';
      }
      ctx.restore();

      ctx.strokeStyle = 'rgba(252,249,246,0.25)';
      ctx.lineWidth = Math.max(2, 1 * k);
      roundRect(ctx, cx, cy, cw, ch, radius);
      ctx.stroke();

      // The badge's own frame, measured.
      var badge = root.querySelector('.nv-embody__badge');
      if (badge) {
        var bb = badge.getBoundingClientRect();
        var bs = getComputedStyle(badge);
        ctx.strokeStyle = 'rgba(252,249,246,0.45)';
        ctx.lineWidth = Math.max(2, 1 * k);
        roundRect(
          ctx,
          cx + (bb.left - box.left) * k,
          cy + (bb.top - box.top) * k,
          bb.width * k,
          bb.height * k,
          parseFloat(bs.borderRadius) * k
        );
        ctx.stroke();
      }

      // Every line of type, where the browser put it.
      var texts = face.querySelectorAll(
        '.nv-embody__micro, .nv-embody__name, .nv-embody__email, .nv-embody__id, .nv-embody__badge'
      );
      ctx.textBaseline = 'alphabetic';
      texts.forEach(function (el) {
        if (!el.textContent.trim() || el.offsetParent === null) return;
        var r = el.getBoundingClientRect();
        var cs = getComputedStyle(el);
        var size = parseFloat(cs.fontSize) * k;
        ctx.font = cs.fontStyle + ' ' + cs.fontWeight + ' ' + size + 'px ' + cs.fontFamily;
        ctx.fillStyle = cs.color;
        var spacing = parseFloat(cs.letterSpacing);
        ctx.letterSpacing = (isNaN(spacing) ? 0 : spacing * k).toFixed(2) + 'px';
        if (cs.textTransform === 'uppercase') ctx.textTransform = null;
        var label = cs.textTransform === 'uppercase' ? el.textContent.trim().toUpperCase() : el.textContent.trim();
        // The email is masked here only: the file is the thing that gets posted.
        if (el.classList.contains('nv-embody__email')) label = maskEmail(label);
        // Centre the text on its own box using the font's real ascent rather than guessing from the
        // line height — that guess is what pushed the badge's word through its frame.
        var padTop = parseFloat(cs.paddingTop) || 0;
        var padLeft = parseFloat(cs.paddingLeft) || 0;
        var m = ctx.measureText(label);
        var ascent = m.actualBoundingBoxAscent || size * 0.72;
        var descent = m.actualBoundingBoxDescent || size * 0.2;
        var innerTop = (r.top - box.top + padTop) * k;
        var innerHeight = (r.height - padTop - (parseFloat(cs.paddingBottom) || 0)) * k;
        var baseline = innerTop + (innerHeight + ascent - descent) / 2;
        ctx.fillText(label, cx + (r.left - box.left + padLeft) * k, cy + baseline);
        ctx.letterSpacing = '0px';
      });

      ctx.textAlign = 'center';
      ctx.font = '500 30px "JetBrains Mono", monospace';
      ctx.fillStyle = 'rgba(252,249,246,0.7)';
      ctx.letterSpacing = '6px';
      ctx.fillText('NUTRAVEY.COM', W / 2, cy + ch + 90);
      ctx.letterSpacing = '0px';
      ctx.textAlign = 'left';

      var logo = face.querySelector('svg');
      if (logo) {
        var lr = logo.getBoundingClientRect();
        return drawLogo(ctx, logo, cx + (lr.left - box.left) * k, cy + (lr.top - box.top) * k, lr.width * k, lr.height * k);
      }
      return Promise.resolve();
    };

    var download = root.querySelector('[data-nv-download]');
    if (download) {
      /* ─── To the story ───
         Phones can hand the PNG to the share sheet, where Instagram offers Story or Post with the
         card already in place — the nearest a web page gets to opening the composer. No site can
         open Instagram's story camera with an image loaded; only a native app can. Where files
         cannot be shared (laptops, older browsers) the button saves the card as before, and its
         label says so. */
      var canShareFiles = function () {
        try {
          if (!navigator.share || !navigator.canShare) return false;
          var probe = new File([new Blob([''], { type: 'image/png' })], 'card.png', { type: 'image/png' });
          return navigator.canShare({ files: [probe] });
        } catch (e) {
          return false;
        }
      };
      if (!canShareFiles()) {
        var downloadLabel = download.querySelector('[data-nv-download-label]');
        if (downloadLabel && download.dataset.fallbackLabel) downloadLabel.textContent = download.dataset.fallbackLabel;
        download.classList.add('is-download');
      }

      download.addEventListener('click', function () {
        if (!pass) return;

        var fileName = 'nutravey-ritual-card-' + pass.id.toLowerCase() + '.png';

        var fail = function (why) {
          if (!error) return;
          error.hidden = false;
          error.textContent = 'The card could not be saved here (' + why + '). Take a screenshot — your place is already kept.';
        };

        var save = function (blob) {
          var url = URL.createObjectURL(blob);
          var a = document.createElement('a');
          a.href = url;
          a.download = fileName;
          a.rel = 'noopener';
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(function () {
            URL.revokeObjectURL(url);
          }, 4000);
        };

        // The share sheet must open within a few seconds of the tap, so nothing waits here that
        // does not have to: the card is drawn, then handed over at once.
        var deliver = function (blob) {
          if (!canShareFiles()) return save(blob);
          var file = new File([blob], fileName, { type: 'image/png' });
          if (!navigator.canShare({ files: [file] })) return save(blob);
          navigator.share({ files: [file], title: 'Nutravey' }).catch(function (e) {
            // Closing the sheet is not a failure. Anything else falls back to the file.
            if (e && e.name === 'AbortError') return;
            save(blob);
          });
        };

        var go = function () {
          Promise.resolve(draw())
            .then(function () {
              if (canvas.toBlob) {
                try {
                  canvas.toBlob(function (blob) {
                    if (blob) deliver(blob);
                    else fail('empty image');
                  }, 'image/png');
                  return;
                } catch (e) {
                  // Falls through to the data URL, which fails the same way if the canvas is
                  // tainted but at least reports it.
                }
              }
              try {
                var data = canvas.toDataURL('image/png');
                var a = document.createElement('a');
                a.href = data;
                a.download = fileName;
                document.body.appendChild(a);
                a.click();
                a.remove();
              } catch (e2) {
                fail(String(e2.name || e2).slice(0, 40));
              }
            })
            .catch(function (e) {
              fail(String(e.name || e).slice(0, 40));
            });
        };

        // Waiting on the brand faces is worth a moment, but not the download: if they never
        // settle the card still has to save.
        if (document.fonts && document.fonts.ready) {
          var raced = false;
          var once = function () {
            if (raced) return;
            raced = true;
            go();
          };
          document.fonts.ready.then(once);
          setTimeout(once, 1200);
        } else {
          go();
        }
      });
    }

    /* Someone who made their card on an earlier page (the launch popup, another day) gets it
       back: the overlay starts out filled, as it is after the water has gone in, so every button
       opens onto the card. */
    var returning = kept();
    if (returning) {
      pass = returning;
      present(pass);
      root.classList.add('is-filled');
      cardMedia.classList.add('is-open');
      // Its cover is gone, so a still of the water is what the card shows until the clip moves.
      if (cardVideo) cardVideo.poster = video.poster;
    }

    /* A shared link with #embody (www.nutravey.com/products/…#embody) opens the overlay as the
       page loads, growing from the page's invitation button as a tap would. Only a button that is
       showing: the launch popup's is hidden until it pops up. */
    var openFromHash = function () {
      if (location.hash.toLowerCase() !== '#embody' || root.classList.contains('is-open')) return;
      var button = Array.prototype.filter.call(document.querySelectorAll('[data-nv-embody-open]'), function (each) {
        return each.getClientRects().length > 0;
      })[0];
      if (button) openFrom(button);
    };
    openFromHash();
    window.addEventListener('hashchange', openFromHash);
  });
})();
